import { Injectable } from "@angular/core";
import { BehaviorSubject, Subscription, interval } from "rxjs";
import { take } from "rxjs/operators";
import { Data } from "@angular/router";
import { VoucherService } from "src/app/services/vouchers.service";
import { NarratorTipService } from "src/app/services/AppRootService/narrator-tip.service";
import { CashPurchaseService } from "src/app/services/cash-purchase.service";
import { MixpanelService } from "src/app/services/mixpanel.service";

export type VoucherDisplayData = Data & {
  isSelected?: boolean;
  isDisabled?: boolean;
  isUsed?: boolean;
  changeExpiryText?: boolean;
  auto_discount?: boolean;
  autoAppliedVoucher?: boolean;
};

@Injectable()
export class CheckoutVoucherService {
  // Raw state
  discountVouchersStatus = false;
  private discountVouchersData: VoucherDisplayData[] = [];
  private selectedDiscountValue = 0;
  private selectedDiscountVoucherData: VoucherDisplayData | null = null;
  private expiryCheckInterval?: Subscription;

  private purchaseDetail: Data | null = null;

  // Reactive state
  private readonly vouchersSubject = new BehaviorSubject<VoucherDisplayData[]>([]);
  readonly vouchers$ = this.vouchersSubject.asObservable();

  private readonly selectedDiscountSubject = new BehaviorSubject<number>(0);
  readonly selectedDiscount$ = this.selectedDiscountSubject.asObservable();

  private readonly isAnyVoucherCheckedSubject = new BehaviorSubject<boolean>(false);
  readonly isAnyVoucherChecked$ = this.isAnyVoucherCheckedSubject.asObservable();

  constructor(
    private voucherService: VoucherService,
    private narratortipService: NarratorTipService,
    private cashPurchaseService: CashPurchaseService
  ) {}

  initialize(purchaseDetail: Data, preferredCurrency: string): void {
    this.purchaseDetail = purchaseDetail;

    this.discountVouchersStatus = !this.purchaseDetail?.offers?.ongoing_flag;

    const storeString = "store";
    const isValidPage = [storeString, "offerofday"].includes(
      (this.purchaseDetail?.page as string)?.toLowerCase()
    );

    if (
      this.discountVouchersStatus &&
      !this.purchaseDetail?.isTopUp &&
      isValidPage &&
      this.purchaseDetail?.discount_available
    ) {
      this.fetchDiscountVouchersData();
    }
  }

  selectVoucher(event: Data, selectedVoucher: VoucherDisplayData, triggerType?: string): void {
    const isSelectedFromEvent = event?.detail?.checked ?? selectedVoucher?.isSelected;
    const isVoucherAutoApplied = selectedVoucher.auto_discount && triggerType === "auto";

    selectedVoucher.autoAppliedVoucher = !!isVoucherAutoApplied;

    if (isSelectedFromEvent) {
      this.selectedDiscountVoucherData = { ...selectedVoucher };

      this.discountVouchersData = this.discountVouchersData.map((voucher) => {
        const isCurrent = voucher.id === selectedVoucher.id;
        return {
          ...voucher,
          isSelected: isCurrent,
          isDisabled: !isCurrent,
          isUsed: !isCurrent,
        };
      });

      this.voucherService
        .getVoucherLookupData(selectedVoucher.voucher_code)
        .pipe(take(1))
        .subscribe((data) => {
          if (data.length > 0) {
            const voucherData: Data = data[0];
            const voucherExpiryDate = voucherData.end_date.toDate().getTime();
            const now = new Date().getTime();

            if (voucherData.status && voucherExpiryDate > now) {
              this.voucherService.updateDiscountVoucherHideStatus(selectedVoucher.id, false);

              this.selectedDiscountValue = (selectedVoucher?.discount_percentage || 0) / 100;
              const finalDiscountVoucherValue =
                this.selectedDiscountValue && this.selectedDiscountValue > 0
                  ? this.selectedDiscountValue
                  : 0;

              if (this.purchaseDetail) {
                this.purchaseDetail["voucher_discount"] = finalDiscountVoucherValue;
              }

              this.cashPurchaseService.selectedDiscountValueSignal.set(finalDiscountVoucherValue);
              this.selectedDiscountSubject.next(finalDiscountVoucherValue);
              this.isAnyVoucherCheckedSubject.next(true);
            } else {
              this.voucherService.showInvalidVoucherToast();
              this.voucherService.updateDiscountVoucherHideStatus(selectedVoucher.id, true);

              this.discountVouchersData = this.discountVouchersData.filter(
                (voucher) => voucher.id !== selectedVoucher.id
              );

              this.discountVouchersData = this.discountVouchersData.map((voucher) => ({
                ...voucher,
                isSelected: false,
                isDisabled: false,
                isUsed: false,
              }));

              this.selectedDiscountValue = 0;
              this.selectedDiscountSubject.next(0);
              this.isAnyVoucherCheckedSubject.next(false);
            }

            this.vouchersSubject.next(this.discountVouchersData);
          }
        });
    } else {
      this.resetVouchersUI();
    }
  }

  resetAll(): void {
    this.resetVouchersUI();
  }

  resetHideStatusOnExit(paymentAttempted: boolean): void {
    if (!paymentAttempted && this.selectedDiscountVoucherData?.id) {
      this.voucherService.updateDiscountVoucherHideStatus(this.selectedDiscountVoucherData.id, false);
    }
  }

  getSelectedVoucherData(): VoucherDisplayData | null {
    return this.selectedDiscountVoucherData;
  }

  getSelectedDiscountValue(): number {
    return this.selectedDiscountSubject.getValue();
  }

  markSelectedVoucherAsRedeemed(): void {
    if (this.selectedDiscountVoucherData) {
      this.voucherService.updateDiscountVoucherRedeemedStatus(this.selectedDiscountVoucherData, true);
    }
  }

  trackVoucherSelectionEvent(mixPanelService: MixpanelService): void {
    const selectedVoucher = this.getSelectedVoucherData();
    const selectedDiscountValue = this.getSelectedDiscountValue();
    const vouchers = this.getVouchersSnapshot();

    if (selectedDiscountValue > 0 && selectedVoucher) {
      const event = selectedVoucher.autoAppliedVoucher
        ? "voucher_auto_applied"
        : "voucher_manual_applied";
      mixPanelService.trackVoucherEvent(event, selectedVoucher);
      return;
    }

    if (vouchers?.length) {
      mixPanelService.trackVoucherEvent("voucher_skipped", vouchers[0]);
    }
  }

  getVouchersSnapshot(): VoucherDisplayData[] {
    return this.vouchersSubject.getValue();
  }

  destroy(): void {
    this.expiryCheckInterval?.unsubscribe();
    this.vouchersSubject.complete();
    this.selectedDiscountSubject.complete();
    this.isAnyVoucherCheckedSubject.complete();
  }

  // Internal helpers
  private fetchDiscountVouchersData(): void {
    this.voucherService
      .getVoucherList()
      .pipe(take(1))
      .subscribe({
        next: (res: Data[]) => {
          const currentTime = new Date().getTime();
          const filteredVouchers = res.filter((voucher) => {
            const voucherExpiryDate = voucher?.expiry_date?.toDate()?.getTime();
            return (
              voucher?.discount_voucher === true &&
              voucher?.isRedeemed === false &&
              voucher?.hide === false &&
              voucherExpiryDate > currentTime &&
              (voucher?.supplier?.toLowerCase() === "general" ||
                voucher?.supplier?.toLowerCase() ===
                  this.purchaseDetail?.real_name?.toLowerCase())
            );
          });

          const hasVouchers = filteredVouchers.length > 1;

          this.discountVouchersData = filteredVouchers.map((voucher, index) => ({
            ...(voucher as VoucherDisplayData),
            isSelected: hasVouchers && index === 0,
            isDisabled: false,
            changeExpiryText: false,
            auto_discount: hasVouchers && index === 0,
            isUsed: false,
          }));

          this.vouchersSubject.next(this.discountVouchersData);

          if (this.discountVouchersData.length > 0 && this.purchaseDetail?.discount_available === true) {
            this.narratortipService.narratorTipEvent("discount_voucher_checkout");

            if (hasVouchers) {
              this.selectVoucher(null as any, this.discountVouchersData[0], "auto");
            }
          }
        },
        error: () => {
          this.discountVouchersData = [];
          this.vouchersSubject.next([]);
        },
      });

    this.startDiscountVouchersExpiryCheck();
  }

  private startDiscountVouchersExpiryCheck(): void {
    try {
      this.expiryCheckInterval?.unsubscribe();

      this.expiryCheckInterval = interval(1000).subscribe(() => {
        const currentTime = new Date().getTime();

        this.discountVouchersData = this.discountVouchersData.filter((voucher) => {
          const voucherExpiryDate = voucher.expiry_date.toDate().getTime();
          const status = voucherExpiryDate > currentTime;
          const remainingTime = voucherExpiryDate - currentTime;

          if (remainingTime > 0 && remainingTime <= 60000) {
            this.updateExpiryTextInVoucher(voucher);
          }

          if (!status) {
            this.resetDiscountAmountOnVoucherExpiry(voucher);
          }

          return status;
        });

        this.vouchersSubject.next(this.discountVouchersData);
      });
    } catch {
      return;
    }
  }

  private resetDiscountAmountOnVoucherExpiry(voucher: VoucherDisplayData): void {
    try {
      const currentVoucherId = this.selectedDiscountVoucherData?.id || "";
      if (voucher?.id === currentVoucherId) {
        this.resetVouchersUI();
        this.voucherService.updateDiscountVoucherHideStatus(currentVoucherId, true);
      }
    } catch {
      return;
    }
  }

  private resetVouchersUI(): void {
    try {
      if (this.selectedDiscountVoucherData?.id) {
        this.voucherService.updateDiscountVoucherHideStatus(this.selectedDiscountVoucherData.id, false);
      }

      this.selectedDiscountValue = 0;
      this.selectedDiscountVoucherData = null;
      this.selectedDiscountSubject.next(0);
      this.isAnyVoucherCheckedSubject.next(false);

      this.discountVouchersData = this.discountVouchersData.map((voucher) => ({
        ...voucher,
        isSelected: false,
        isDisabled: false,
        isUsed: false,
      }));

      this.vouchersSubject.next(this.discountVouchersData);
    } catch {
      return;
    }
  }

  private updateExpiryTextInVoucher(expiredVoucher: VoucherDisplayData): void {
    const vouchers = this.discountVouchersData.slice();
    const voucherIndex = vouchers.findIndex((voucher) => voucher.id === expiredVoucher.id);

    if (voucherIndex !== -1) {
      vouchers[voucherIndex] = {
        ...vouchers[voucherIndex],
        changeExpiryText: true,
      };

      this.discountVouchersData = vouchers;
      this.vouchersSubject.next(this.discountVouchersData);
    }
  }
}


